import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import { FileEntity } from '../../types';
import { accordionSummarySx, accordionSx } from './auditDetailShared';
import { buildReportRows, ReportRow, reportRowFileIds } from './reportRows';

interface AuditReportsSectionProps {
  isDark: boolean;
  expanded: boolean;
  files: FileEntity[];
  loadingFiles: boolean;
  saving: boolean;
  findingsCount: number;
  loadingFindings: boolean;
  onToggle: (_: React.SyntheticEvent, isExpanded: boolean) => void;
  onGenerateReport: () => void;
  onDownload: (fileId: number) => void;
  onDeleteReport: (fileIds: number[]) => void;
}

const AuditReportsSection: React.FC<AuditReportsSectionProps> = ({
  isDark,
  expanded,
  files,
  loadingFiles,
  saving,
  findingsCount,
  loadingFindings,
  onToggle,
  onGenerateReport,
  onDownload,
  onDeleteReport,
}) => {
  const rows = buildReportRows(files);

  return (
  <Accordion expanded={expanded} onChange={onToggle} sx={accordionSx(isDark)}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummarySx(isDark)}>
      <DescriptionOutlinedIcon sx={{ fontSize: 20, color: 'primary.main' }} />
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
        Reports
      </Typography>
      {rows.length > 0 && (
        <Chip
          label={rows.length}
          size="small"
          sx={{
            ml: 1,
            fontWeight: 700,
            fontSize: '0.7rem',
            height: 22,
            minWidth: 28,
            bgcolor: isDark ? 'rgba(66,165,245,0.12)' : 'rgba(66,165,245,0.08)',
            color: '#42a5f5',
            border: '1px solid rgba(66,165,245,0.3)',
          }}
        />
      )}
    </AccordionSummary>
    <AccordionDetails sx={{ px: 3, py: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#1a1f3c' : '#f5f7fa',
          border: `1px solid ${isDark ? '#2e3560' : '#dfe3e8'}`,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Ergebnisbericht generieren
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem' }}>
            Erstellt einen vollständigen Bericht mit Management Summary, Schwachstellenübersicht,
            Vorbemerkungen und allen dokumentierten Findings.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={
            saving ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <DescriptionOutlinedIcon />
            )
          }
          onClick={onGenerateReport}
          disabled={saving || loadingFindings || findingsCount === 0}
          sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        >
          {saving ? 'Generiert...' : 'Report generieren'}
        </Button>
      </Box>

      {findingsCount === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Es sind noch keine Schwachstellen dokumentiert. Fügen Sie zuerst Schwachstellen hinzu,
          bevor Sie einen Report generieren.
        </Alert>
      )}

      {loadingFiles ? (
        <Box sx={{ py: 1 }}>
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton key={index} variant="rounded" height={48} sx={{ mb: 1 }} />
          ))}
        </Box>
      ) : rows.length === 0 ? (
        <Typography variant="body2" color="text.disabled" sx={{ fontSize: '0.85rem' }}>
          Noch keine Reports generiert.
        </Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dateiname</TableCell>
                <TableCell>Größe</TableCell>
                <TableCell>Generiert von</TableCell>
                <TableCell>Generiert am</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <ReportTableRow
                  key={row.key}
                  row={row}
                  onDownload={onDownload}
                  onDeleteReport={onDeleteReport}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </AccordionDetails>
  </Accordion>
  );
};

const formatSize = (bytes?: number): string | null => {
  if (!bytes) return null;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const formatRowSize = (row: ReportRow): string => {
  if (row.word && row.pdf) {
    return [formatSize(row.word.dateigroesse), formatSize(row.pdf.dateigroesse)]
      .map((size) => size ?? '-')
      .join(' / ');
  }
  return formatSize(row.files[0]?.dateigroesse) ?? '-';
};

const rowAuthor = (row: ReportRow): string => {
  const author = row.files.find((file) => file.generiert_von)?.generiert_von;
  return author ? `${author.vorname} ${author.nachname}` : '-';
};

const rowCreatedAt = (row: ReportRow): string => {
  const stamps = row.files
    .map((file) => file.erstellt_am)
    .filter((value): value is string => Boolean(value))
    .sort();
  if (stamps.length === 0) return '-';
  return new Date(stamps[0]).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ReportTableRow: React.FC<{
  row: ReportRow;
  onDownload: (fileId: number) => void;
  onDeleteReport: (fileIds: number[]) => void;
}> = ({ row, onDownload, onDeleteReport }) => {
  const fileIds = reportRowFileIds(row);
  const paired = Boolean(row.word && row.pdf);

  return (
    <TableRow hover>
      <TableCell sx={{ fontWeight: 500 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
          <Box>
            {row.dateiname}
            {paired && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Word und PDF
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell>{formatRowSize(row)}</TableCell>
      <TableCell>{rowAuthor(row)}</TableCell>
      <TableCell>{rowCreatedAt(row)}</TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          {row.word && (
            <Button size="small" onClick={() => onDownload(row.word!.id)}>
              Word
            </Button>
          )}
          {row.pdf && (
            <Button size="small" onClick={() => onDownload(row.pdf!.id)}>
              PDF
            </Button>
          )}
          {!row.word && !row.pdf && (
            <Button size="small" onClick={() => onDownload(row.files[0].id)}>
              Herunterladen
            </Button>
          )}
          <Tooltip title={paired ? 'Word und PDF löschen' : 'Report löschen'}>
            <IconButton
              size="small"
              color="error"
              aria-label={paired ? 'Word und PDF löschen' : 'Report löschen'}
              onClick={() => onDeleteReport(fileIds)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default AuditReportsSection;
